import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSocket } from '../context/SocketContext';
import {
  copyToClipboard,
  createProjectBoardSocket,
  flattenChecklistItems,
  formatDate,
  formatOptions,
  getPlainText,
  selectLoader
} from 'utils/Helpers';
import { fetchEnumOptions, fetchMilestoneDetail, fetchSubtaskDetail } from '../utils/fetchData';
import Activity from './Activity/Activity';
import Description from './Description/Description';
import MileStoneSetup from './MilestoneSetup/MileStoneSetup';
import TaskWrapper from './Task/TaskWrapper';
import './MilestoneModal.css';
import HeadingWithIcon from '../shared/HeadingWithIcon';
import { CHECK_SQUARE2_ICON } from '../assets/image';
import SubtaskIcon from 'assets/img/Subtaskicon.svg';
import Arrotop from 'assets/img/arro-top.svg';
import LinkIcon from 'assets/img/linkicon.svg';
import Dropdown from 'react-bootstrap/Dropdown';
import boardToast from '../utils/boardToast';
import { TOAST_TYPE } from 'utils/AppToast';
import Attachments from './Attachments/Attachments';
import backIcon2 from 'assets/img/back-icon2.svg';

const MilestoneModal = ({
  projectTitle,
  show,
  onHide,
  milestoneId,
  milestoneMembers,
  disabledMode,
  projectBoardId,
  channels,
  isSubtaskOpen,
  selectedSubtaskId,
  showChecklist = false,
  priorityOptions,
  isTemplateActive = false,
  projectDetail,
  statusOptions,
  isProjectRelatedBoard,
  defaultMilestoneLabels,
  isMasterCalender = false,
  user,
  selectedMilestone,
  masterCalenderSocket,
  reminderOptions,
  toggleModal,
  formatTeamData,
  isArchivedParent = false
}) => {
  const socket = useSocket();
  const checklistScrollRef = useRef(null);
  // const [statusOptions, setStatusOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [milestone, setMilestone] = useState();
  const [milestoneName, setMilestoneName] = useState('');
  const [subTask, setSubTask] = useState();
  const [subTaskId, setSubTaskId] = useState('');
  const [showSubTask, setShowSubTask] = useState(false);
  const [isSubTask, setSubtask] = useState(true);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [totalSubtasks, setTotalSubtasks] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [showSubTaskTab, setShowSubTaskTab] = useState(true);
  const [showAttachmentTab, setShowAttachmentTab] = useState(false);
  const [updatedDesc, setUpdatedDesc] = useState('');
  const [isClosedStatus, setClosedStatus] = useState(false);
  const [defaultLabels, setDefaultLabels] = useState([]);
  const [viewMode, setViewMode] = useState(disabledMode || false);
  const [members, setMembers] = useState([]);
  const [isChecklistFullscreen, setIsChecklistFullscreen] = useState(false);
  const modalRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeout = useRef(null);
  useEffect(() => {
    if (channels && channels.length > 0) {
      const formattedOptions = formatOptions(channels);
      setChannelOptions(formattedOptions);
    }
  }, [channels]);

  useEffect(() => {
    if (milestoneMembers && milestoneMembers.length > 0) {
      setMembers(milestoneMembers);
    }
  }, [milestoneMembers]);

  useEffect(() => {
    if (defaultMilestoneLabels && defaultMilestoneLabels.length > 0) {
      setDefaultLabels(defaultMilestoneLabels);
    }
  }, [defaultMilestoneLabels]);

  useEffect(() => {
    if (showChecklist) {
      setShowSubTaskTab(false);
      setSubtask(false);
    }
  }, [showChecklist]);
  useEffect(() => {
    if (showSubTask) {
      setShowSubTaskTab(false);
      setShowAttachmentTab(false);
    }
  }, [showSubTask]);

  useEffect(() => {
    return () => {
      setShowSubTask(false);
    };
  }, []);

  // Function to handle clicks outside the modal
  const handleOutsideClick = useCallback(
    (event) => {
      if (!modalRef.current) return;

      const clickedOutside =
        !modalRef.current.contains(event.target) &&
        !document.querySelector('.confirm-modal-wrapper')?.contains(event.target);

      if (!clickedOutside) return;

      // ⭐ ONLY close when fullscreen
      if (isChecklistFullscreen) {
        setIsChecklistFullscreen(false);
        return;
      }

      // existing behavior (optional)
      if (milestone?._id) {
        saveTaskSubtaskonClose();
      }
    },
    [milestone?._id, updatedDesc, projectBoardId, socket, isChecklistFullscreen, onHide]
  );

  const saveTaskSubtaskonClose = () => {
    let masterSocket = socket;
    let selectedBoardId = projectBoardId;
    if (isMasterCalender) {
      masterSocket = createProjectBoardSocket(user._id, milestone?.projectBoardId?._id || milestone?.projectBoardId);
      selectedBoardId = milestone?.projectBoardId?._id || milestone?.projectBoardId;
    }
    if (milestone && updatedDesc != milestone?.kbDescription) {
      if (showSubTask && updatedDesc != subTask?.description) {
        masterSocket?.emit(
          'update-subtask',
          {
            id: subTaskId,
            milestoneId: milestone._id,
            payload: { description: updatedDesc },
            projectBoardId: selectedBoardId
          }
          // selectedBoardId
        );
      } else {
        masterSocket?.emit('update-milestone', {
          id: milestone?._id,
          payload: { kbDescription: updatedDesc },
          projectBoardId: selectedBoardId
        });
      }
    }
  };
  useEffect(() => {
    if (show) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [handleOutsideClick, show]);

  const fetchMilestone = useCallback(async () => {
    if (milestoneId) {
      const data = await fetchMilestoneDetail(milestoneId);
      if (data) {
        setMilestone(data);
        setMilestoneName(data?.name);
        setClosedStatus(data?.milestoneKBStatus?.status?.value === 'close');
        setDefaultLabels(data?.projectBoardId?.labels || []);
        if (data?.projectBoardId?.channels) {
          const formattedOptions = formatOptions(data?.projectBoardId?.channels);
          setChannelOptions(formattedOptions);
        }
        let userPermission = data?.projectBoardId?.teams.find((x) => x.userId._id === user._id)?.permission?.name;
        if (!disabledMode) {
          setViewMode(userPermission === 'View');
        }
        setMembers(formatTeamData(data?.projectBoardId?.teams));
        const hasAttachments = (data?.attachments?.length ?? 0) > 0;
        const hasSubtasks = (data?.subtask?.length ?? 0) > 0;
        const hasChecklist = (data?.checklist?.length ?? 0) > 0;
        setTimeout(() => {
          if (!showChecklist) {
            if (hasAttachments) {
              setShowAttachmentTab(true);
              setShowSubTaskTab(false);
            } else if (!hasAttachments && hasSubtasks) {
              setShowAttachmentTab(false);
              setShowSubTaskTab(true);
              setSubtask(true);
            } else if (!hasSubtasks && hasChecklist) {
              setShowAttachmentTab(false);
              setShowSubTaskTab(false);
              setSubtask(false);
            } else if (!hasAttachments && !hasSubtasks && !hasChecklist) {
              setShowAttachmentTab(true);
              setShowSubTaskTab(false);
              setSubtask(false);
            }
          }
        }, 50);
      }
    }
  }, [milestoneId]);

  useEffect(() => {
    if (milestone && !showSubTask) {
      setUpdatedDesc(milestone?.kbDescription);
    }
  }, [milestone]);

  const fetchSubtask = async (subtaskId) => {
    setSubtask(false);
    if (subtaskId) {
      setSubTaskId(subtaskId);
      const data = await fetchSubtaskDetail(subtaskId);
      if (data) {
        // console.log(data);

        setSubTask(data);
        setShowSubTask(true);
        setUpdatedDesc(data?.description);
        setSubtask(false);

        setTimeout(() => {
          if (!showChecklist) {
            if (data?.attachments.length > 0) {
              setShowAttachmentTab(true);
              setShowSubTaskTab(false);
            } else if (data?.attachments.length == 0 && data?.checklist.length > 0) {
              setShowAttachmentTab(false);
              setShowSubTaskTab(false);
            } else if (data?.attachments.length == 0 && data?.checklist.length == 0) {
              setShowAttachmentTab(true);
              setShowSubTaskTab(false);
            }
          }
        }, 100);
        // setMilestone(data);
      }
    }
  };

  useEffect(() => {
    // fetchStatusOptions();

    if (isTemplateActive && projectDetail.milestones && projectDetail.milestones.length > 0) {
      const selectedMilestone = projectDetail.milestones.find((milestone) => milestone._id === milestoneId);
      // console.log(selectedMilestone, 'selectedMilestone');

      setMilestone(selectedMilestone);
      setMilestoneName(selectedMilestone?.name);
      setClosedStatus(selectedMilestone?.milestoneKBStatus?.status?.value === 'close');
      if (!showSubTask) setUpdatedDesc(selectedMilestone?.kbDescription);
    } else if (!showSubTask) {
      fetchMilestone();
    }
    if (isSubtaskOpen) {
      setSubTaskId(selectedSubtaskId);
      fetchSubtask(selectedSubtaskId);
    }
    console.log(selectedMilestone);
  }, [fetchMilestone, projectDetail, selectedMilestone]);

  useEffect(() => {
    let mastersocket = socket;
    if (isMasterCalender) {
      mastersocket = masterCalenderSocket;
    }
    const handle_milestone_detail = (data) => {
      if (data?._id === milestoneId) {
        setMilestone(data);
        // console.log(data, 'detaillllllllllllllllll');
        setClosedStatus(data?.milestoneKBStatus?.status?.value === 'close');
        if (!showSubTask) setUpdatedDesc(data?.kbDescription);
        // console.log('milestone-detail', data);
      }
    };

    const handle_subtask_detail = (data) => {
      if (data?._id === subTaskId) {
        // console.log('subtask-detail', data);
        setUpdatedDesc(data?.description);
        setSubTask(data);
      }
    };

    const handle_milestone_updated = (data) => {
      // console.log(data, 'milestone-updated');

      if (data.projectBoardId === projectBoardId) {
        console.log(data);
      }
    };
    mastersocket?.on('milestone-detail', handle_milestone_detail);
    mastersocket?.on('subtask-detail', handle_subtask_detail);
    mastersocket?.on('milestone-updated', handle_milestone_updated);
    return () => {
      mastersocket?.off('milestone-detail', handle_milestone_detail);
      mastersocket?.off('subtask-detail', handle_subtask_detail);
      mastersocket?.off('milestone-updated', handle_milestone_updated);
    };
  }, [milestoneId, socket, subTaskId]);

  const handleUpdateMilestone = async (data) => {
    let masterSocket = socket;
    let selectedBoardId = projectBoardId;
    if (isMasterCalender) {
      masterSocket = createProjectBoardSocket(user._id, milestone?.projectBoardId._id || milestone?.projectBoardId);
      selectedBoardId = milestone?.projectBoardId._id || milestone?.projectBoardId;
    }
    if (showSubTask) {
      masterSocket?.emit(
        'update-subtask',
        { id: subTaskId, milestoneId: milestone._id, payload: { description: data }, projectBoardId: selectedBoardId }
        // selectedBoardId
      );
    } else {
      masterSocket?.emit('update-milestone', {
        id: milestone._id,
        payload: { kbDescription: data },
        projectBoardId: selectedBoardId
      });
    }
  };

  const handleDelete = () => {
    let masterSocket = socket;
    let selectedBoardId = projectBoardId;
    if (isMasterCalender) {
      masterSocket = createProjectBoardSocket(user._id, milestone?.projectBoardId._id || milestone?.projectBoardId);
      selectedBoardId = milestone?.projectBoardId._id || milestone?.projectBoardId;
    }
    if (showSubTask) {
      masterSocket?.emit('delete-subtask', { id: subTaskId, milestoneId }, selectedBoardId);
    } else {
      masterSocket?.emit('delete-milestone', { id: milestoneId, projectBoardId: selectedBoardId });
      onHide();
    }
    setShowSubTask(false);
    setSubTaskId('');
    setSubtask('');
    setShowDropdown(false);
    boardToast(`${showSubTask ? 'Subtask' : 'Milestone'} deleted successfully.`, TOAST_TYPE.ERROR);
  };

  const CustomMenu = React.forwardRef(({ style, className, 'aria-labelledby': labeledBy }, ref) => (
    <div
      ref={ref}
      style={{ ...style, padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '10px' }}
      className={className}
      aria-labelledby={labeledBy}>
      <div style={{ padding: '0px' }} className="dropdown-custom-content">
        <div class="triangle"></div>
        <h2>
          <strong>Delete this?</strong>
        </h2>
        <p>This action is permanent and cannot be undone.</p>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}
          className="dropdown-custom-btn">
          <button onClick={() => setShowDropdown(false)} className="cancel-btn">
            Cancel
          </button>
          <button onClick={handleDelete} className="delete-btn">
            Delete
          </button>
        </div>
      </div>
    </div>
  ));

  const flattenedSubtaskChecklist = flattenChecklistItems(subTask?.checklist);
  const flattenedChecklist = flattenChecklistItems(checklistItems);

  // ✅ counts
  const checklistCompletedCount = showSubTask
    ? flattenedSubtaskChecklist?.filter((x) => x?.isCompleted)?.length
    : flattenedChecklist?.filter((x) => x?.isCompleted)?.length;

  const checklistTotalCount = showSubTask ? flattenedSubtaskChecklist?.length : flattenedChecklist?.length;

  const hideSubtask = () => {
    setShowSubTask(false);
    setSubTaskId('');
    setSubtask(true);
    const hasAttachments = (milestone?.attachments?.length ?? 0) > 0;
    const hasSubtasks = (milestone?.subtask?.length ?? 0) > 0;
    const hasChecklist = (milestone?.checklist?.length ?? 0) > 0;
    setTimeout(() => {
      if (!showChecklist) {
        if (hasAttachments) {
          setShowAttachmentTab(true);
          setShowSubTaskTab(false);
        } else if (!hasAttachments && hasSubtasks) {
          setShowAttachmentTab(false);
          setShowSubTaskTab(true);
          setSubtask(true);
        } else if (!hasSubtasks && hasChecklist) {
          setShowAttachmentTab(false);
          setShowSubTaskTab(false);
          setSubtask(false);
        } else if (!hasAttachments && !hasSubtasks && !hasChecklist) {
          setShowAttachmentTab(true);
          setShowSubTaskTab(false);
          setSubtask(false);
        }
      }
    }, 50);
  };

  const handleScroll = () => {
    setIsScrolling(true);

    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 800); // hide after user stops scrolling
  };

  useEffect(() => {
    if (!isChecklistFullscreen) return;

    const scrollEl = checklistScrollRef.current;
    if (!scrollEl) return;

    // wait for fullscreen layout
    setTimeout(() => {
      scrollEl.scrollTop = 0;
    }, 60);
  }, [isChecklistFullscreen]);

  return (
    <Modal
      show={show}
      centered
      dialogClassName={isChecklistFullscreen ? 'checklist-modal-900' : ''}
      onHide={() => {
        onHide();
        saveTaskSubtaskonClose();
      }}
      enforceFocus={false}
      restoreFocus={false}
      backdrop="static"
      keyboard={false}
      className="miltestone-modal-wrapper miltestone-modal-wrapper2">
      <div ref={modalRef} className="mileStoneSetup_modal_cover">
        {!isChecklistFullscreen && (
          <Modal.Header closeButton={true}>
            <div className="header-top-content">
              <div className="header-left-text">
                <img
                  src={backIcon2}
                  alt="backIcon2 icon"
                  className="backIcon2-img cursor_pointer"
                  onClick={() => {
                    if (showSubTask) {
                      hideSubtask();
                    } else {
                      onHide();
                    }
                  }}
                />
                {milestone && (
                  <p>
                    {projectTitle} /{' '}
                    <span
                      className="cursor_pointer parent_span_milestone"
                      onClick={() => {
                        if (showSubTask) {
                          setShowSubTask(false);
                          setSubTaskId('');
                          setSubtask(true);
                        }
                      }}>
                      {milestoneName}
                      {!showSubTask && (
                        <img
                          src={LinkIcon}
                          alt="Link"
                          className="linkIcon"
                          title="Copy task link"
                          onClick={() => {
                            const link = `/project-board/${isProjectRelatedBoard ? `project` : `board`}/${
                              milestone?.projectId?._id || milestone?.projectBoardId._id || milestone?.projectBoardId
                            }/${milestone?._id}`;
                            copyToClipboard(link);
                          }}
                        />
                      )}
                    </span>
                    {showSubTask && (
                      <>
                        {' '}
                        /{' '}
                        <span className="parent_span_subtask">
                          {getPlainText(subTask?.name)}{' '}
                          <img
                            src={LinkIcon}
                            alt="Link"
                            className="linkIcon"
                            title="Copy subtask link"
                            onClick={() => {
                              const link = `/project-board/${isProjectRelatedBoard ? `project` : `board`}/${
                                milestone?.projectId?._id || milestone?.projectBoardId._id || milestone?.projectBoardId
                              }/${milestone?._id}/${subTask?._id}`;
                              copyToClipboard(link);
                            }}
                          />
                        </span>
                        {/* Subtask LinkIcon */}
                      </>
                    )}
                  </p>
                )}
              </div>
              {isTemplateActive && (
                <h2 className="">
                  <span className="preview-title">Preview</span>
                </h2>
              )}
              <div className="header-right-text">
                <p>
                  Created on{' '}
                  {showSubTask
                    ? formatDate(subTask?.createdDate || new Date())
                    : formatDate(milestone?.createdDate || new Date())}
                </p>
                {/* {showSubTask && ( */}
                {/* <> */}
                {viewMode ?  "" : <span>&nbsp;</span>}
                {viewMode ? (
                  ''
                ) : (
                  <Dropdown
                    align="start"
                    drop="down"
                    containerPadding={10}
                    container="body"
                    className="cursor_pointer delete_subtask_drp"
                    show={showDropdown}
                    onToggle={(isOpen) => setShowDropdown(isOpen)}>
                    <Dropdown.Toggle id="dropdown-custom-components">Delete</Dropdown.Toggle>
                    <Dropdown.Menu
                      as={CustomMenu}
                      style={{
                        width: '350px',
                        border: 'none',
                        marginTop: '10px',
                        zIndex: 9999
                      }}
                      className="dropdown-custom-components-popup"
                    />
                  </Dropdown>
                )}
                {/* </>
              )} */}
              </div>
            </div>
          </Modal.Header>
        )}
        <Modal.Body>
          {!milestone ? (
            <div className="nodata-loader">{selectLoader(60)} </div>
          ) : (
            <>
              <div className="row">
                <div className={isChecklistFullscreen ? 'col-12' : 'col-lg-7 col-md-12 col-sm-12 col-xs-12'}>
                  <div
                    ref={checklistScrollRef}
                    onScroll={handleScroll}
                    className={`main-left-scrollable-div ${isChecklistFullscreen ? 'screen-expanded' : ''} ${isScrolling ? 'scroll-active' : ''}`}>
                    {!isChecklistFullscreen && (
                      <div className="mb-3">
                        <Modal.Title>
                          {milestone && (
                            <MileStoneSetup
                              milestone={showSubTask ? subTask : milestone}
                              selectedMilestone={milestone}
                              priorityOptions={priorityOptions}
                              statusOptions={showSubTask ? statusOptions : channelOptions}
                              members={members}
                              viewMode={viewMode}
                              createdBy={milestone.createdBy?.fullName}
                              projectBoardId={projectBoardId}
                              showSubTask={showSubTask}
                              milestoneName={milestoneName}
                              subtaskId={subTaskId}
                              hideSubtask={() => {
                                hideSubtask();
                              }}
                              onHide={onHide}
                              projectTitle={projectTitle}
                              defaultLabels={defaultLabels}
                              isMasterCalender={isMasterCalender}
                              user={user}
                              reminderOptions={reminderOptions}
                              showProfileModal={() => {
                                onHide();
                                toggleModal('profileModal', true);
                              }}
                              isClosedStatus={isClosedStatus}
                            />
                          )}
                        </Modal.Title>
                      </div>
                    )}
                    <div className={`left-scrollable-div ${isChecklistFullscreen ? 'screen-expanded' : ''}`}>
                      <div className="mb-3">
                        {milestone && !isChecklistFullscreen && (
                          <Description
                            viewMode={viewMode || isClosedStatus}
                            description={showSubTask ? subTask?.description : milestone?.kbDescription}
                            handleSubmitDescription={handleUpdateMilestone}
                            handleDescriptionChange={(data) => {
                              if (subTask) {
                                setSubTask((prevState) => ({
                                  ...prevState,
                                  description: data
                                }));
                              } else {
                                setMilestone((prevState) => ({
                                  ...prevState,
                                  kbDescription: data
                                }));
                              }
                            }}
                            showTextEditor={showTextEditor}
                            setShowTextEditor={setShowTextEditor}
                            isDesc={true}
                            setUpdatedDesc={setUpdatedDesc}
                            parentId={milestoneId}
                            isSubtask={showSubTask}
                          />
                        )}
                      </div>

                      {!isChecklistFullscreen && (
                        <ul className="nav nav-tabs mb-2 tasktabs mt-4" id="taskTab" role="tablist">
                          <li className="nav-item" role="presentation">
                            <button
                              className={`nav-link ${showAttachmentTab ? 'active' : ''}`}
                              onClick={() => {
                                setShowTextEditor(false);
                                setSubtask(false);
                                setShowAttachmentTab(true);
                              }}>
                              <HeadingWithIcon
                                icon={CHECK_SQUARE2_ICON}
                                heading="Attachments"
                                isAttachment={true}
                                count={showSubTask ? subTask?.attachments?.length : milestone?.attachments?.length}
                                // totalCount={checklistItems?.length}
                              />
                            </button>
                          </li>
                          {!showSubTask && (
                            <li className="nav-item" role="presentation">
                              <button
                                className={`nav-link ${showSubTaskTab && !showAttachmentTab ? 'active' : ''}`}
                                onClick={() => {
                                  setShowTextEditor(false);
                                  setSubtask(true);
                                  setShowSubTaskTab(true);
                                  setShowAttachmentTab(false);
                                }}>
                                <HeadingWithIcon
                                  icon={SubtaskIcon}
                                  heading="Subtask"
                                  count={totalSubtasks?.filter((x) => x?.status?.value === 'complete')?.length}
                                  totalCount={totalSubtasks?.length}
                                />
                              </button>
                            </li>
                          )}
                          <li className="nav-item" role="presentation">
                            <button
                              className={`nav-link ${!showSubTaskTab && !showAttachmentTab ? 'active' : ''}`}
                              onClick={() => {
                                setShowTextEditor(false);
                                setSubtask(false);
                                setShowSubTaskTab(false);
                                setShowAttachmentTab(false);
                              }}>
                              <HeadingWithIcon
                                icon={CHECK_SQUARE2_ICON}
                                heading="Checklists"
                                // count={milestone?.checklist?.length}
                                totalCount={showSubTask ? subTask?.checklist?.length : milestone?.checklist?.length}
                                isChecklist={true}
                              />
                            </button>
                          </li>
                        </ul>
                      )}
                      {/* Tab Content */}
                      <div className="tab-content" id="taskTabContent">
                        {showAttachmentTab ? (
                          <Attachments
                            parentDetails={showSubTask ? subTask : milestone}
                            parentId={showSubTask ? subTaskId : milestoneId}
                            isSubtask={showSubTask}
                            viewMode={viewMode || isClosedStatus}
                          />
                        ) : showSubTaskTab ? (
                          <TaskWrapper
                            members={members}
                            milestoneId={milestoneId}
                            viewMode={viewMode || isClosedStatus}
                            projectBoardId={projectBoardId}
                            isSubTask={isSubTask}
                            statusOptions={statusOptions}
                            fetchSubtask={fetchSubtask}
                            setTotalSubtasks={setTotalSubtasks}
                            setChecklistItems={setChecklistItems}
                            isTemplateActive={isTemplateActive}
                            selectedMilestone={milestone}
                            milestone={milestone}
                            isMasterCalender={isMasterCalender}
                            masterCalenderSocket={masterCalenderSocket}
                            reminderOptions={reminderOptions}
                            isClosedStatus={isClosedStatus}
                            isChecklistFullscreen={isChecklistFullscreen}
                            setIsChecklistFullscreen={setIsChecklistFullscreen}
                            // milestone={milestone}
                          />
                        ) : showSubTask ? (
                          <TaskWrapper
                            members={members}
                            milestoneId={milestoneId}
                            viewMode={viewMode || isClosedStatus}
                            projectBoardId={projectBoardId}
                            isSubTask={isSubTask}
                            statusOptions={statusOptions}
                            subTaskId={subTaskId}
                            fetchSubtask={fetchSubtask}
                            showSubTask={showSubTask}
                            subTask={subTask}
                            setChecklistItems={setChecklistItems}
                            selectedMilestone={milestone}
                            isMasterCalender={isMasterCalender}
                            masterCalenderSocket={masterCalenderSocket}
                            milestone={milestone}
                            reminderOptions={reminderOptions}
                            isChecklistFullscreen={isChecklistFullscreen}
                            setIsChecklistFullscreen={setIsChecklistFullscreen}
                          />
                        ) : (
                          <TaskWrapper
                            members={members}
                            milestoneId={milestoneId}
                            viewMode={viewMode || isClosedStatus}
                            projectBoardId={projectBoardId}
                            isSubTask={isSubTask}
                            statusOptions={statusOptions}
                            subTaskId={subTaskId}
                            fetchSubtask={fetchSubtask}
                            showSubTask={showSubTask}
                            setTotalSubtasks={setTotalSubtasks}
                            setChecklistItems={setChecklistItems}
                            selectedMilestone={milestone}
                            milestone={milestone}
                            isMasterCalender={isMasterCalender}
                            masterCalenderSocket={masterCalenderSocket}
                            reminderOptions={reminderOptions}
                            isChecklistFullscreen={isChecklistFullscreen}
                            setIsChecklistFullscreen={setIsChecklistFullscreen}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* {!isTemplateActive && ( */}
                {!isChecklistFullscreen && (
                  <div className="col-lg-5 col-md-12 col-sm-12 col-xs-12">
                    <div className="mt-30">
                      <Activity
                        members={members}
                        milestoneId={showSubTask ? subTaskId : milestoneId}
                        viewMode={viewMode}
                        projectBoardId={projectBoardId}
                        defaultActivities={showSubTask ? subTask?.activities : milestone.activities}
                        showSubTask={showSubTask}
                        subtaskId={subTaskId}
                      />
                    </div>
                  </div>
                )}
                {/* )} */}
              </div>
            </>
          )}
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default MilestoneModal;
